import React, { useRef, useState } from 'react';
import { LuDownload } from 'react-icons/lu';
import { useAutoSave } from '../../hooks/useAutoSave';
import { Button, Dialog, Field, Input, SaveStatus, Select } from '../../components/ui';
import { t } from '../../i18n';
import { formatCpf } from '../../lib/formatters';
import { birthDateError, cardValidityError, cpfError } from '../../lib/validators';

export interface PatientFormData {
  nome: string;
  dataNasc: string;
  sexo: string;
  cpf: string;
  convenio: string;
  numCarteirinha: string;
  valCarteirinha: string;
}

interface PatientDataDialogProps {
  onClose: () => void;
  onDownload: (data: PatientFormData) => void;
  onFormChange?: (data: PatientFormData) => void;
  initialData?: Partial<PatientFormData>;
  timestamp?: string;
}

export const PatientDataDialog: React.FC<PatientDataDialogProps> = ({
  onClose,
  onDownload,
  onFormChange,
  initialData = {},
  timestamp,
}) => {
  const [form, setForm] = useState<PatientFormData>({
    nome: initialData.nome ?? '',
    dataNasc: initialData.dataNasc ?? '',
    sexo: initialData.sexo ?? '',
    cpf: initialData.cpf ?? '',
    convenio: initialData.convenio ?? '',
    numCarteirinha: initialData.numCarteirinha ?? '',
    valCarteirinha: initialData.valCarteirinha ?? '',
  });

  // O erro só aparece depois que a pessoa sai do campo, não enquanto digita.
  const [touched, setTouched] = useState({ cpf: false, birth: false, card: false });
  const touch = (field: keyof typeof touched) => () => setTouched((prev) => ({ ...prev, [field]: true }));
  // CPF incompleto ou inválido não vai para o backend; o /chat/save mescla os campos, então o último válido fica salvo.
  const savedCpf = cpfError(form.cpf) ? undefined : form.cpf;
  const cpfRef = useRef<HTMLInputElement>(null);
  const birthRef = useRef<HTMLInputElement>(null);

  const { autoSave, status: saveStatus, savedAt } = useAutoSave(
    {
      timestamp,
      pacient_name: form.nome,
      dataNasc: form.dataNasc,
      sexo: form.sexo,
      cpf: savedCpf,
      convenio: form.convenio,
      numCarteirinha: form.numCarteirinha,
      valCarteirinha: form.valCarteirinha,
    },
    [form.nome, form.dataNasc, form.sexo, savedCpf, form.convenio, form.numCarteirinha, form.valCarteirinha]
  );

  const set = (field: keyof PatientFormData) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const value = field === 'cpf' ? formatCpf(e.target.value) : e.target.value;
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      onFormChange?.(next);
      return next;
    });
  };

  // Salva na hora ao fechar: o debounce de 1,5s pode não ter disparado ainda.
  const handleClose = () => {
    autoSave();
    onClose();
  };

  // Campo vazio não trava (a guia sai sem ele), mas CPF ou nascimento inválido não vai para o PDF.
  const handleDownload = () => {
    const invalid = cpfError(form.cpf) ? cpfRef : birthDateError(form.dataNasc) ? birthRef : null;
    if (invalid) {
      setTouched({ cpf: true, birth: true, card: true });
      invalid.current?.focus();
      return;
    }
    autoSave();
    onDownload(form);
  };

  return (
    <Dialog
      open
      onClose={handleClose}
      title={t('savedOrders.patient.title')}
      description={<SaveStatus status={saveStatus} savedAt={savedAt} />}
      footer={
        <Button variant="primary" fullWidth icon={<LuDownload aria-hidden />} onClick={handleDownload}>
          {t('savedOrders.patient.download')}
        </Button>
      }
    >
      <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2">
        <Field label={t('savedOrders.patient.name')} className="sm:col-span-2">
          <Input value={form.nome} onChange={set('nome')} autoComplete="off" />
        </Field>
        <Field label={t('savedOrders.patient.birth')} error={touched.birth ? birthDateError(form.dataNasc) : null}>
          <Input ref={birthRef} type="date" min="1900-01-01" max={new Date().toISOString().slice(0, 10)} value={form.dataNasc} onChange={set('dataNasc')} onBlur={touch('birth')} />
        </Field>
        <Field label={t('savedOrders.patient.sex')}>
          <Select value={form.sexo} onChange={set('sexo')}>
            <option value="" disabled>{t('savedOrders.patient.select')}</option>
            <option value="F">{t('savedOrders.patient.female')}</option>
            <option value="M">{t('savedOrders.patient.male')}</option>
          </Select>
        </Field>
        <Field label={t('savedOrders.patient.cpf')} error={touched.cpf ? cpfError(form.cpf) : null}>
          <Input ref={cpfRef} value={form.cpf} onChange={set('cpf')} onBlur={touch('cpf')} placeholder="000.000.000-00" maxLength={14} inputMode="numeric" />
        </Field>
        <Field label={t('savedOrders.patient.plan')}>
          <Input value={form.convenio} onChange={set('convenio')} />
        </Field>
        <Field label={t('savedOrders.patient.cardNumber')}>
          <Input value={form.numCarteirinha} onChange={set('numCarteirinha')} />
        </Field>
        <Field label={t('savedOrders.patient.cardValidity')} error={touched.card ? cardValidityError(form.valCarteirinha) : null}>
          <Input type="month" value={form.valCarteirinha} onChange={set('valCarteirinha')} onBlur={touch('card')} />
        </Field>
      </div>
    </Dialog>
  );
};
